import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import type { Account } from '@/widgets/Wizard/models/accounts';
import type { WizardOptions } from '@/widgets/Wizard/models/time-table/wizard-options';
import type { AccommodationExtra, AvailableAccommodation, MetaData } from '@/widgets/Wizard/models/wizard';
import { useReservationStore } from './reservation';
import type { TommyGateway } from '../gateways/tommy';
import type { FormConfig } from '@/common/store/BookStore';
import { getFormConfig } from '../modules/tommy3/forms';
import { usePetsAmenities } from '../composables/amenities';

export const useMetadataStore = defineStore('booking-wizard/meta-data', () => {
    let tommy: TommyGateway | undefined = undefined;

    const reservationStore = useReservationStore();
    const { reservedPets } = storeToRefs(reservationStore);

    // State
    const activeAccount = ref<Account | null>(null);
    const wizardOptionsOverride = ref<WizardOptions | null>(null);
    const tommyMetaData = ref<MetaData | null>(null);
    const tommyCountries = ref<{ code: string; name: string }[]>([]);
    const availableAccommodations = ref<AvailableAccommodation[] | null>(null);
    const faultyAvailableAccommodations = ref<AvailableAccommodation[]>([]);
    const accommodationExtras = ref<{ [key: string]: AccommodationExtra[] }>({});
    const formConfig = ref<FormConfig | null>(null);

    // Getters
    const getWizardOptions = computed(() => {
        if (activeAccount.value === null) {
            return null;
        }

        return overrideAccountWizardOptions(activeAccount.value, wizardOptionsOverride.value);
    });

    const accommodations = computed(() => {
        if (!tommyMetaData.value) {
            return [];
        }

        return tommyMetaData.value.accommodations;
    });

    const ageCategories = computed(() => {
        if (!tommyMetaData.value) {
            return [];
        }

        return tommyMetaData.value.ageCategories;
    });

    const map = computed(() => {
        if (!tommyMetaData.value) {
            return null;
        }

        return tommyMetaData.value.map;
    });

    const getAccommodationExtras = computed(() => {
        return (accommodationId: number) => {
            const extras = accommodationExtras.value[accommodationId.toString()] ?? [];

            return extras.filter((extra) => {
                return !Object.keys(reservedPets.value).includes(extra.id.toString());
            });
        };
    });

    // Actions
    const setTommy = (gateway: TommyGateway) => {
        tommy = gateway;
    };

    const getTommyGateway = (): TommyGateway => {
        if (!tommy) {
            throw new Error('Can\'t make tommy API call since no tommy gateway is present');
        }

        return tommy;
    };

    const updateFormConfig = async (locale: string) => {
        formConfig.value = await getFormConfig(locale);
    };

    const updateAccommodationExtras = async () => {
        if (!tommy) {
            throw new Error('Can\'t make tommy API call since no tommy gateway is present');
        }

        const reservationStore = useReservationStore();
        const { departureDate, arrivalDate, reservedAgeGroups, chosenAccommodation } = storeToRefs(reservationStore);

        if (!arrivalDate.value || !departureDate.value || !chosenAccommodation.value) {
            return;
        }

        const salesChannel = activeAccount.value ? activeAccount.value.options.salesChannel : undefined;

        const { amenities } = usePetsAmenities();

        const extras = await tommy.getAccommodationBookingExtras(
            arrivalDate.value,
            departureDate.value,
            chosenAccommodation.value.id,
            amenities.value,
            reservedAgeGroups.value,
            undefined,
            salesChannel,
        );

        if (!extras) {
            return;
        }

        accommodationExtras.value[chosenAccommodation.value.id.toString()] = extras;
    };

    const updateCountries = async (locale: string) => {
        if (!tommy) {
            return;
        }

        tommyCountries.value = await tommy.getCountries(locale) ?? [];
        await updateFormConfig(locale);
    };

    const markAvailableAccommodationAsFaulty = (accommodation: AvailableAccommodation) => {
        if (faultyAvailableAccommodations.value.includes(accommodation)) {
            return;
        }

        faultyAvailableAccommodations.value.push(accommodation);
    };

    const reset = () => {
        availableAccommodations.value = null;
        faultyAvailableAccommodations.value = [];
        accommodationExtras.value = {};
        formConfig.value = null;
    };

    return {
        // State
        activeAccount,
        wizardOptionsOverride,
        tommyMetaData,
        tommyCountries,
        availableAccommodations,
        faultyAvailableAccommodations,
        accommodationExtras,
        formConfig,

        // Getters
        getWizardOptions,
        accommodations,
        ageCategories,
        map,
        getAccommodationExtras,

        // Actions
        setTommy,
        getTommyGateway,
        updateFormConfig,
        updateAccommodationExtras,
        updateCountries,
        markAvailableAccommodationAsFaulty,
        reset,
    };
});

function overrideAccountWizardOptions(account: Account, overrideOptions: WizardOptions | null): Account['options'] {
    if (overrideOptions === null) {
        return account.options;
    }

    const options = account.options;

    options.usePin = overrideOptions.usePin;
    options.salesChannel =
        overrideOptions.salesChannel !== undefined ? overrideOptions.salesChannel : options.salesChannel;

    return options;
}
